import React, { useState } from 'react'
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";
import Header from "components/layout/Header";
import UserDashboard from 'components/dashboards/UserDashboard';
import ViewAsUser from 'components/dashboards/ViewAsUser';
import { useSelector } from 'react-redux';
import { DashboardOutlined, Visibility, Close } from '@mui/icons-material';
import { useGetAgentByIdQuery } from 'state/managementApi';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';

const Dashboard = () => {
  const userData = useSelector((state) => state.user);
  const theme = useTheme();
  const [superadminViewRole, setSuperadminViewRole] = useState('superadmin');
  const [viewAsUserId, setViewAsUserId] = useState(null);
  const [showViewAsSelector, setShowViewAsSelector] = useState(false);
  
  // Fetch the viewed user's data if viewing as someone
  const { data: viewedUser, isLoading: viewedUserLoading } = useGetAgentByIdQuery(viewAsUserId, {
    skip: !viewAsUserId,
  });
  
  // Determine which user data to display
  const displayUser = viewAsUserId && viewedUser ? viewedUser : userData;
  const isViewingAsOther = viewAsUserId && viewedUser;
  
  // Check if current user can view as others
  const canViewAsOthers = userData?.role && ['manager', 'admin', 'superadmin'].includes(userData.role);

  const toggleSuperadminView = () => {
    setSuperadminViewRole((prevRole) => {
      switch (prevRole) {
        case 'agent':
          return 'dispatch';
        case 'dispatch':
          return 'manager';
        case 'manager':
          return 'admin';
        case 'admin':
          return 'superadmin';
        case 'superadmin':
          return 'agent';
        default:
          return 'agent';
      }
    });
  };
  
  const handleViewAsUser = (userId) => {
    setViewAsUserId(userId);
    setShowViewAsSelector(false);
  };
  
  const handleExitViewAs = () => {
    setViewAsUserId(null);
    setShowViewAsSelector(false);
  };
  
  const handleToggleViewAsSelector = () => {
    setShowViewAsSelector(!showViewAsSelector);
  };

  if (!userData) return <Skeleton animation="wave" variant='rounded' width='90%' height='70%' sx={{ margin: '1.5rem 2.5rem', padding: 2 }} />
  if (viewAsUserId && viewedUserLoading) return <Skeleton animation="wave" variant='rounded' width='90%' height='70%' sx={{ margin: '1.5rem 2.5rem', padding: 2 }} />

  return (
    <>
      {!userData.role ? (
        <Skeleton animation="wave" variant='rounded' width='90%' height='70%' sx={{ margin: '1.5rem 2.5rem', padding: 2 }} />
      ) : (
        <Box>
          {/* View As Alert Banner */}
          {isViewingAsOther && (
            <Box m="1.5rem 2.5rem 0rem 2.5rem">
              <Alert 
                severity="info"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={handleExitViewAs}
                  >
                    <Close fontSize="inherit" />
                  </IconButton>
                }
                sx={{ fontWeight: 'bold' }}
              >
                Viewing dashboard as: {viewedUser.name} ({viewedUser.role})
              </Alert>
            </Box>
          )}
          
          <Box m="1.5rem 2.5rem" sx={{ backgroundColor: theme.palette.background.alt, padding: 2, borderRadius: '7px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', }}>
                <DashboardOutlined sx={{ marginRight: '5px', fontSize: '50px', paddingBottom: '10px' }} />
                <Header title={`${displayUser.role.replace(/^[a-z]/, match => match.toUpperCase())} Dashboard`} subtitle={isViewingAsOther ? `Viewing as ${displayUser.name}` : `Logged in as ${userData.name}`} />
              </Box>
              {canViewAsOthers && !isViewingAsOther && (
                <Button
                  variant="contained"
                  startIcon={<Visibility />}
                  onClick={handleToggleViewAsSelector}
                  sx={{ height: 'fit-content' }}
                >
                  View As Agent
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={{ backgroundColor: theme.palette.background.alt, padding: 2, borderRadius: '7px', margin: '0rem 2.5rem' }}>
            <Box sx={{ width: '100%', }}>
              {/* Show View As User Selector */}
              {showViewAsSelector && canViewAsOthers && (
                <Box sx={{ marginBottom: 2 }}>
                  <ViewAsUser 
                    onUserSelect={handleViewAsUser} 
                    onExitView={handleToggleViewAsSelector}
                  />
                </Box>
              )}
              
              {!showViewAsSelector && (
                <>
                  {userData.role === 'superadmin' && !isViewingAsOther && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: 'black',
                          color: '#34e718',
                        }}
                        onClick={toggleSuperadminView}>
                        Toggle Superadmin View
                      </Button>
                      <Typography>{superadminViewRole.toUpperCase()}</Typography>
                    </Box>
                  )}
                  <UserDashboard 
                    userRole={isViewingAsOther ? displayUser.role : userData.role} 
                    superadminViewRole={superadminViewRole}
                    viewedUser={isViewingAsOther ? displayUser : null}
                    viewedUserId={viewAsUserId}
                  />
                </>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Dashboard;